import { Activity, LogOut, Monitor, Moon, ServerCog, Sun, UserRound, Users } from 'lucide-react';
import { useCurrentUser, useLogout } from '@/api/hooks/auth';
import { Menu, type MenuEntry } from '@/components';
import { paths } from '@/routing';
import { useThemeStore, type ThemePreference } from '@/stores/theme';
import styles from './TopBar.module.css';

function initials(name: string, email: string): string {
  const source = name.trim() || email;
  const parts = source.split(/[\s@._-]+/).filter(Boolean);
  return ((parts[0]?.[0] ?? '?') + (parts[1]?.[0] ?? '')).toUpperCase();
}

const THEMES: { value: ThemePreference; label: string; icon: typeof Sun }[] = [
  { value: 'system', label: 'System theme', icon: Monitor },
  { value: 'light', label: 'Light theme', icon: Sun },
  { value: 'dark', label: 'Dark theme', icon: Moon },
];

/** Avatar menu: account, admin pages, theme, sign out. */
export function UserMenu() {
  const me = useCurrentUser();
  const logout = useLogout();
  const preference = useThemeStore((s) => s.preference);
  const setPreference = useThemeStore((s) => s.setPreference);

  const items: MenuEntry[] = [
    { heading: me.email },
    { label: 'Account', icon: <UserRound />, to: paths.account() },
    ...(me.is_admin
      ? ([
          'separator',
          { heading: 'Administration' },
          { label: 'Users', icon: <Users />, to: paths.adminUsers() },
          { label: 'Jobs', icon: <Activity />, to: paths.adminJobs() },
          { label: 'System', icon: <ServerCog />, to: paths.adminSystem() },
        ] satisfies MenuEntry[])
      : []),
    'separator',
    ...THEMES.map(({ value, label, icon: Icon }) => ({
      label,
      icon: <Icon />,
      checked: preference === value,
      onSelect: () => setPreference(value),
    })),
    'separator',
    { label: 'Sign out', icon: <LogOut />, onSelect: () => logout.mutate() },
  ];

  return (
    <Menu
      label="User menu"
      items={items}
      trigger={(props) => (
        <button
          type="button"
          className={styles.user}
          aria-label={`User menu for ${me.name || me.email}`}
          {...props}
        >
          <span className={styles.avatar} aria-hidden>
            {initials(me.name, me.email)}
          </span>
          <span className={styles.userName}>{me.name || me.email}</span>
        </button>
      )}
    />
  );
}
